import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ChevronRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal, MaskLines } from "@/components/Reveal";
import { Icon, WhatsAppIcon } from "@/components/Icon";
import { WA_DEFAULT } from "@/data/site";
import { serviceBySlug } from "@/data/services";
import { industryBySlug } from "@/data/industries";

export const Img = ({ name, alt, eager = false, sizes = "(min-width: 1024px) 50vw, 100vw", className = "photo" }) => (
  <img
    src={`/img/${name}.webp`}
    srcSet={`/img/${name}-sm.webp 800w, /img/${name}.webp 1800w`}
    sizes={sizes}
    alt={alt}
    width="1800"
    height="1200"
    loading={eager ? "eager" : "lazy"}
    fetchPriority={eager ? "high" : undefined}
    decoding="async"
    className={className}
  />
);

export const Breadcrumbs = ({ items }) => (
  <nav aria-label="Breadcrumb" className="mb-8" data-testid="breadcrumbs">
    <ol className="flex flex-wrap items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.16em] text-graphite">
      {items.map(([name, path], i) => (
        <li key={path} className="flex items-center gap-1.5">
          {i > 0 && <ChevronRight className="h-3 w-3" aria-hidden="true" />}
          {i < items.length - 1 ? <Link to={path} className="hover:text-rust-ink">{name}</Link> : <span aria-current="page" className="text-charcoal">{name}</span>}
        </li>
      ))}
    </ol>
  </nav>
);

export const SectionHead = ({ eyebrow, title, lead, light = false, className = "" }) => (
  <Reveal className={`max-w-3xl ${className}`}>
    {eyebrow && <p className={`eyebrow ${light ? "eyebrow-light" : ""}`}>{eyebrow}</p>}
    <h2 className={`h2 mt-5 ${light ? "!text-ivory" : ""}`}>{title}</h2>
    {lead && <p className={`lead mt-6 ${light ? "!text-[#D9D5CB]" : ""}`}>{lead}</p>}
  </Reveal>
);

export const PageHero = ({ eyebrow, lines, intro, img, imgAlt, crumbs, children }) => (
  <section className="relative overflow-hidden border-b border-stone" data-testid="page-hero">
    <div className="grid-lines absolute inset-0" aria-hidden="true" />
    <div className="wrap relative grid gap-12 pb-16 pt-12 lg:grid-cols-[1.15fr_0.85fr] lg:items-end lg:pb-24 lg:pt-16">
      <div>
        {crumbs && <Breadcrumbs items={crumbs} />}
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <MaskLines lines={lines} className="mt-6 text-4xl font-extrabold leading-[1.02] sm:text-5xl lg:text-6xl" testId="page-title" />
        {intro && <Reveal delay={0.4}><p className="lead mt-8 max-w-2xl">{intro}</p></Reveal>}
        {children && <Reveal delay={0.5} className="mt-10 flex flex-wrap gap-3">{children}</Reveal>}
      </div>
      {img && (
        <div className="relative aspect-[4/3] overflow-hidden rounded-md lg:aspect-[4/5]">
          <Img name={img} alt={imgAlt} eager sizes="(min-width: 1024px) 40vw, 100vw" />
          <div className="absolute inset-0 ring-1 ring-inset ring-charcoal/10" aria-hidden="true" />
        </div>
      )}
    </div>
  </section>
);

export const FaqList = ({ faqs, idPrefix = "faq" }) => (
  <Accordion type="single" collapsible className="border-t border-charcoal/15" data-testid={`${idPrefix}-accordion`}>
    {faqs.map(([q, a], i) => (
      <AccordionItem key={q} value={`${idPrefix}-${i}`} className="border-b border-charcoal/15">
        <AccordionTrigger className="gap-6 py-6 text-left font-display text-lg font-semibold hover:no-underline sm:text-xl [&>svg]:h-5 [&>svg]:w-5 [&>svg]:text-rust-deep" data-testid={`${idPrefix}-q-${i}`}>
          <span className="flex gap-5"><span className="font-mono text-xs font-normal text-rust-ink pt-1.5">{String(i + 1).padStart(2, "0")}</span>{q}</span>
        </AccordionTrigger>
        <AccordionContent className="pb-6 pl-10 pr-6 text-[15px] leading-relaxed text-graphite">{a}</AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
);

export const CtaBand = ({ title = "Ready to Modernize Your Industrial Business?", text = "Tell us about your business, your current challenges, and the technology you need. Our team can help identify a practical digital solution for your next stage of growth." }) => (
  <section className="relative overflow-hidden bg-charcoal text-ivory grain" data-testid="cta-band">
    <div className="grid-lines-dark absolute inset-0" aria-hidden="true" />
    <div className="pointer-events-none absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full border border-rust/30" aria-hidden="true" />
    <div className="pointer-events-none absolute -right-20 -top-20 h-[360px] w-[360px] rounded-full border border-rust/20" aria-hidden="true" />
    <div className="wrap relative py-24 lg:py-32">
      <Reveal className="max-w-4xl">
        <p className="eyebrow eyebrow-light">Start a conversation</p>
        <h2 className="mt-6 text-4xl font-extrabold leading-[1.02] text-ivory sm:text-5xl lg:text-6xl">{title}</h2>
        <p className="mt-8 max-w-2xl text-base leading-relaxed text-[#D9D5CB] md:text-lg">{text}</p>
        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/contact#inquiry" className="btn-primary hover:!bg-ivory hover:!text-charcoal" data-testid="cta-discuss-btn">Discuss Your Project <ArrowRight className="h-4 w-4" /></Link>
          <a href={WA_DEFAULT} target="_blank" rel="noopener noreferrer" className="btn-light" data-testid="cta-whatsapp-btn"><WhatsAppIcon className="h-4 w-4" /> Contact Us on WhatsApp</a>
        </div>
      </Reveal>
    </div>
  </section>
);

export const Marquee = ({ items, dark = false }) => (
  <div className={`relative overflow-hidden border-y py-8 ${dark ? "border-white/10 bg-charcoal" : "border-stone bg-ivory"}`} aria-hidden="true" data-testid="editorial-marquee">
    <div className="flex w-max animate-marquee">
      {[0, 1].map((k) => (
        <div key={k} className="flex shrink-0 items-center">
          {items.map((t) => (
            <span key={t + k} className="flex items-center">
              <span className={`px-8 font-display text-4xl font-extrabold uppercase tracking-tight sm:text-6xl lg:text-7xl ${dark ? "text-ivory" : "outline-text"}`} style={{ fontStretch: "125%" }}>{t}</span>
              <svg viewBox="0 0 24 24" className="h-6 w-6 text-rust sm:h-8 sm:w-8" fill="currentColor"><path d="M12 0l2.5 9.5L24 12l-9.5 2.5L12 24l-2.5-9.5L0 12l9.5-2.5z" /></svg>
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
);

export const ServiceCard = ({ s, i, big = false, wide = false }) => (
  <Link
    to={`/services/${s.slug}`}
    data-testid={`service-card-${s.slug}`}
    className={`group relative flex w-full flex-col overflow-hidden rounded-md border p-6 transition-[transform,box-shadow,border-color] duration-500 hover:-translate-y-1 hover:shadow-[0_30px_60px_-35px_rgba(39,41,35,.5)] sm:p-8 ${big ? "min-h-[360px] sm:min-h-[420px] border-charcoal bg-charcoal text-ivory" : "border-stone bg-white hover:border-charcoal/30"}`}
  >
    {big && (
      <div className="absolute inset-0 opacity-40 transition-opacity duration-700 group-hover:opacity-55" aria-hidden="true">
        <Img name={s.img} alt="" sizes="50vw" className="photo transition-transform duration-[1.4s] group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/70 to-transparent" />
      </div>
    )}
    <div className="relative flex items-start justify-between">
      <span className={`flex h-12 w-12 items-center justify-center rounded-full border ${big ? "border-ivory/30 text-[#E8B795]" : "border-stone text-rust-deep"} transition-colors duration-300 group-hover:border-rust group-hover:bg-rust-deep group-hover:text-white`}>
        <Icon name={s.icon} className="h-5 w-5" />
      </span>
      <span className={`font-mono text-xs ${big ? "text-[#CFCBC0]" : "text-graphite"}`}>{String(i + 1).padStart(2, "0")}</span>
    </div>
    <div className={`relative mt-auto pt-6 sm:pt-10 ${big ? "lg:pt-40" : ""}`}>
      <h3 className={`font-display font-bold leading-tight ${big ? "text-3xl text-ivory sm:text-4xl" : "text-xl"}`}>{s.title}</h3>
      <p className={`mt-3 text-[14px] leading-relaxed ${big ? "max-w-md text-[#D9D5CB]" : "text-graphite"}`}>{s.short}</p>
      <span className={`mt-5 inline-flex items-center gap-1.5 text-[13px] font-semibold ${big ? "text-[#E8B795]" : "text-rust-ink"}`}>
        Explore service <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
      </span>
    </div>
  </Link>
);

export const IndustryCard = ({ ind, i }) => (
  <Link to={`/industries/${ind.slug}`} data-testid={`industry-card-${ind.slug}`} className="group relative block aspect-[4/5] overflow-hidden rounded-md bg-charcoal">
    <Img name={ind.img} alt={`${ind.name} facility`} sizes="(min-width: 1024px) 33vw, 100vw" className="photo transition-transform duration-[1.4s] ease-out group-hover:scale-[1.06]" />
    <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/30 to-transparent" aria-hidden="true" />
    <div className="absolute inset-x-0 bottom-0 p-6 text-ivory">
      <span className="font-mono text-xs text-[#E8B795]">{String(i + 1).padStart(2, "0")} / Sector</span>
      <h3 className="mt-2 font-display text-2xl font-bold leading-tight">{ind.name}</h3>
      <span className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold text-ivory/90">View solutions <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></span>
    </div>
  </Link>
);

export const RelatedLinks = ({ services = [], industries = [] }) => (
  <section className="wrap py-20 lg:py-28" data-testid="related-links">
    <div className="grid gap-12 lg:grid-cols-2">
      {services.length > 0 && (
        <div>
          <p className="eyebrow">Related services</p>
          <ul className="mt-6 border-t border-charcoal/15">
            {services.map((slug) => serviceBySlug[slug] && (
              <li key={slug}>
                <Link to={`/services/${slug}`} className="group flex items-center justify-between border-b border-charcoal/15 py-5 font-display text-lg font-semibold transition-colors hover:text-rust-ink" data-testid={`related-service-${slug}`}>
                  {serviceBySlug[slug].title} <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
      {industries.length > 0 && (
        <div>
          <p className="eyebrow">Related industries</p>
          <ul className="mt-6 border-t border-charcoal/15">
            {industries.map((slug) => industryBySlug[slug] && (
              <li key={slug}>
                <Link to={`/industries/${slug}`} className="group flex items-center justify-between border-b border-charcoal/15 py-5 font-display text-lg font-semibold transition-colors hover:text-rust-ink" data-testid={`related-industry-${slug}`}>
                  {industryBySlug[slug].name} <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  </section>
);

export const Steps = ({ steps, dark = false }) => (
  <ol className="grid gap-px overflow-hidden rounded-md border border-stone bg-stone md:grid-cols-2 lg:grid-cols-5" data-testid="process-steps">
    {steps.map(([t, d], i) => (
      <Reveal as="li" key={t} delay={i * 0.08} className={`relative p-7 ${dark ? "bg-charcoal" : "bg-white"}`}>
        <span className="font-mono text-xs text-rust-ink">STEP {String(i + 1).padStart(2, "0")}</span>
        <h3 className="mt-8 font-display text-2xl font-bold">{t}</h3>
        <p className="mt-3 text-[14px] leading-relaxed text-graphite">{d}</p>
      </Reveal>
    ))}
  </ol>
);
